import {
  clearExpiringTabToken,
  getExpiringTabToken,
  setExpiringTabToken,
} from '@/lib/expiring-tab-token';

/**
 * Holds the Bearer token for an ACTIVE IMPERSONATION SESSION, scoped to the
 * one tab that started it.
 *
 * `apps/app/src/lib/auth-token.ts` holds the admin's own identity: a
 * page-singleton, optionally persisted (native Keychain, or `localStorage` in
 * dev) so it survives restarts and is shared across every tab — correct for
 * a normal session, wrong for an impersonation one. Two tabs impersonating
 * different users (or one impersonating while another stays as the admin)
 * need genuinely independent identities, and neither a module variable
 * (wiped by impersonate's hard `window.location.href` reload) nor
 * `localStorage` (shared across tabs) gives you that.
 *
 * `sessionStorage` does: it survives the reload, only the tab that wrote it
 * can read it, and it disappears when that tab closes. `getRequestAuthToken`
 * (`request-auth-token.ts`) checks this store BEFORE the normal bearer/cookie
 * path, for `apiClient` and every hand-rolled fetch alike, so a tab holding
 * this token overrides whatever the shared session cookie or admin bearer
 * currently say, on every platform (including production web, where normal
 * auth is cookie-only — see `api-client.ts`).
 *
 * The impersonate call no longer sets the shared session cookie for this
 * client at all (ENG-1529): it used to, and every OTHER tab on the origin —
 * one that never went near the admin terminal — silently became the
 * impersonated customer. This token is now the ONLY place the impersonated
 * identity lives in the browser.
 *
 * The server issues this token with the 3-hour
 * `impersonationSessionDuration` (`packages/auth/src/server.ts`), and
 * `stop-impersonating` revokes it explicitly (ENG-893) rather than relying on
 * expiry alone. This module mirrors that bound client-side
 * (`IMPERSONATION_TOKEN_MAX_AGE_MS`): `sessionStorage` surviving a reload is
 * the point, but it also survives the browser's cookies being cleared, so the
 * tab needs its own clock rather than the server's alone. When it runs out,
 * `consumeExpiredImpersonation` sends the tab back to the admin panel instead
 * of letting it fall through to the shared cookie as a different identity.
 */
const KEY = 'borradh.impersonation.bearerToken';

/**
 * Non-secret marker that THIS tab started an impersonation. It outlives the
 * token on purpose: when the token expires, the marker is how the tab knows it
 * was impersonating and must not just carry on as whatever identity the
 * fallback path resolves to (ENG-1529).
 */
const ACTIVE_KEY = 'borradh.impersonation.active';

/**
 * Matches the server's initial `impersonationSessionDuration` (3 hours,
 * `packages/auth/src/server.ts`): the tab stops using the token three hours
 * after storing it. The admin's own tab token keeps the
 * shorter default. It is a 7-day session; this one dies in three hours.
 */
export const IMPERSONATION_TOKEN_MAX_AGE_MS = 3 * 60 * 60 * 1000;

export const getImpersonationToken = (): string | undefined =>
  getExpiringTabToken(KEY);

export const setImpersonationToken = (token: string): void => {
  setExpiringTabToken(KEY, token, IMPERSONATION_TOKEN_MAX_AGE_MS);
  try {
    window.sessionStorage.setItem(ACTIVE_KEY, '1');
  } catch {
    // Storage unavailable — the token write above failed the same way.
  }
};

export const clearImpersonationToken = (): void => {
  clearExpiringTabToken(KEY);
  try {
    window.sessionStorage.removeItem(ACTIVE_KEY);
  } catch {
    // Nothing to clear.
  }
};

/**
 * True exactly once for a tab whose impersonation ran out: it was
 * impersonating (marker set) but no usable token is left. Clears the marker,
 * so the caller's "send them back to the admin panel" runs a single time.
 */
export const consumeExpiredImpersonation = (): boolean => {
  let active: string | null;
  try {
    active = window.sessionStorage.getItem(ACTIVE_KEY);
  } catch {
    return false;
  }
  if (!active || getImpersonationToken()) return false;
  clearImpersonationToken();
  return true;
};

const EXPIRED_NOTICE_KEY = 'borradh.impersonation.expiredNotice';

/**
 * End an impersonation that ran out client-side: leave a one-shot notice for
 * the admin panel and hard-navigate there. A full load (not a router push) so
 * nothing cached under the customer's identity survives into the admin view.
 */
export const endExpiredImpersonation = (): void => {
  try {
    window.sessionStorage.setItem(EXPIRED_NOTICE_KEY, '1');
  } catch {
    // The redirect alone still gets the admin out.
  }
  window.location.assign('/admin');
};

/** True once after `endExpiredImpersonation`, for the admin panel's toast. */
export const takeImpersonationExpiredNotice = (): boolean => {
  try {
    if (!window.sessionStorage.getItem(EXPIRED_NOTICE_KEY)) return false;
    window.sessionStorage.removeItem(EXPIRED_NOTICE_KEY);
    return true;
  } catch {
    return false;
  }
};
